import { useEffect, useRef } from "react";

import { useTrackRecordingView } from "../api/track-recording-view";

type UseRecordingViewTrackerProps = {
  recordingId?: string;
  isPlaying: boolean;
  currentTime: number;
};

export function useRecordingViewTracker({
  recordingId,
  isPlaying,
  currentTime,
}: UseRecordingViewTrackerProps) {
  const trackRecordingView = useTrackRecordingView();

  const hasTrackedView = useRef<boolean>(false);
  const lastTrackedTime = useRef<number>(0);

  useEffect(() => {
    if (!recordingId) {
      return;
    }

    // Only track if we haven't already tracked this view and we're playing
    if (!hasTrackedView.current && isPlaying && currentTime >= 30) {
      // Check if we've watched at least 30 seconds
      const watchedTime = Math.min(currentTime, 30); // Cap at 30 seconds for tracking

      trackRecordingView.mutate({
        recordingId,
        viewDurationSeconds: Math.floor(watchedTime),
        startPositionSeconds: 0,
        endPositionSeconds: Math.floor(currentTime),
      });

      hasTrackedView.current = true;
      lastTrackedTime.current = currentTime;
    }
  }, [isPlaying, currentTime, recordingId, trackRecordingView]);

  // Reset tracking when video is reset (currentTime goes back to 0)
  useEffect(() => {
    if (currentTime < lastTrackedTime.current) {
      hasTrackedView.current = false;
      lastTrackedTime.current = 0;
    }
  }, [currentTime]);

  return {
    hasTrackedView: hasTrackedView.current,
  };
}
